import { useReducer, useEffect, useState } from 'react';
import { questions, envSciQuestions, gcseQuestions } from '../data/questions';
import { emptySteps } from '../config/archetypes';

const API_BASE = process.env.REACT_APP_API_URL;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const GEOGRAPHY_TOPICS = {
  physical: ['water_carbon', 'deserts', 'hazards'],
  human: ['global_systems', 'changing_places', 'resource_security'],
};

const FOUR_MARKER_DURATION       = 1500;  // 25 minutes
const FULL_MOCK_DURATION         = 9000;  // 2 hours 30 minutes
const ENV_SHORT_DURATION         = 1500;  // 25 minutes
const ENV_MIXED_DURATION         = 5400;  // 90 minutes
const ENV_TOPIC_DURATION         = 2700;  // 45 minutes — env-sci Topic Mock
const GCSE_SCIENCE_SHORT_DURATION = 1500;  // 25 minutes
const GCSE_SCIENCE_MIXED_DURATION = 2100;  // 35 minutes

const GCSE_SCIENCE_SUBJECTS = new Set(['biology', 'chemistry', 'physics']);
const ENV_TOPIC_TARGET_SHORT_MARKS = 12;
const ENV_TOPIC_NINE_COUNT  = 2;

// Archetype priority order for full mock question selection per topic
const FULL_MOCK_ARCHETYPES = ['outline_4', 'assess_6', 'data_6', 'scaffold_9', 'essay_20'];

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function selectFourMarkerQuestions(geographyType) {
  const allowedTopics = GEOGRAPHY_TOPICS[geographyType] ?? GEOGRAPHY_TOPICS.physical;
  const pool = questions.filter(
    (q) => q.archetype === 'outline_4' && allowedTopics.includes(q.topic),
  );
  return shuffleArray(pool).slice(0, 6);
}

function selectEnvSciShortSprint() {
  const pool = envSciQuestions.filter((q) => q.archetype === 'envsci_short');
  return shuffleArray(pool).slice(0, 6);
}

function selectEnvSciMixedMock() {
  const shorts  = shuffleArray(envSciQuestions.filter((q) => q.archetype === 'envsci_short')).slice(0, 3);
  const nines   = shuffleArray(envSciQuestions.filter((q) => q.archetype === 'envsci_9')).slice(0, 1);
  const twenties = shuffleArray(envSciQuestions.filter((q) => q.archetype === 'envsci_25')).slice(0, 1);
  return [...shorts, ...nines, ...twenties];
}

// Recast a 20/25-mark essay as a 9-marker for the Topic Mock: the existing
// envsci_9 wizard cards and marking pipeline apply, with a rescale instruction
// added on the server (see prompts.ts) when `original_marks` is present.
function recastAsNineMark(q) {
  return {
    ...q,
    archetype: 'envsci_9',
    marks: 9,
    original_marks: q.marks,
    original_archetype: q.archetype,
  };
}

// Priority sort: never-answered first, then fewest attempts, then longest since
// last attempt, then random tiebreak so two equally-fresh questions don't always
// come out in the same order.
function priorityCompare(a, b, history) {
  const ha = history[a.id];
  const hb = history[b.id];
  const ca = ha?.count ?? 0;
  const cb = hb?.count ?? 0;
  if (ca !== cb) return ca - cb;
  const la = ha?.lastSeenAt ? Date.parse(ha.lastSeenAt) : 0;
  const lb = hb?.lastSeenAt ? Date.parse(hb.lastSeenAt) : 0;
  if (la !== lb) return la - lb;
  return Math.random() - 0.5;
}

// Walk the short-question pool in priority order; pick until the running total
// reaches at least `target` marks. Returns whatever was selected (may total
// less than target if the pool is small).
function pickShortsToReachTarget(pool, target) {
  const picked = [];
  let total = 0;
  for (const q of pool) {
    if (total >= target) break;
    picked.push(q);
    total += q.marks ?? 0;
  }
  return picked;
}

// Pick a 30-mark env-science paper drawn from all topics within a content
// group (e.g. group '3.1' covers topics 3.1.1, 3.1.2, 3.1.3 → "Living
// Environment"). Returns { questions, totalMarks } or { error } if the group
// doesn't have at least two nine-mark candidates (true 9-markers + 20/25-mark
// essays recast as 9-markers).
function questionGroup(q) {
  return (q.topic ?? '').split('.').slice(0, 2).join('.');
}

function selectEnvSciTopicMock(group, history) {
  const inGroup = envSciQuestions.filter((q) => questionGroup(q) === group);
  const shorts  = inGroup.filter((q) => q.archetype === 'envsci_short');
  const trueNine = inGroup.filter((q) => q.archetype === 'envsci_9');
  const recastNine = inGroup
    .filter((q) => q.archetype === 'envsci_25')
    .map(recastAsNineMark);
  const ninePool = [...trueNine, ...recastNine];

  if (ninePool.length < ENV_TOPIC_NINE_COUNT) {
    return { error: 'not_enough_nine_markers', available: ninePool.length };
  }

  // priorityCompare already randomises ties, so sorting the cross-topic pool
  // gives an even spread across all topics within the group.
  const sortedNines  = [...ninePool].sort((a, b) => priorityCompare(a, b, history));
  const sortedShorts = [...shorts].sort((a, b) => priorityCompare(a, b, history));

  const chosenNines  = sortedNines.slice(0, ENV_TOPIC_NINE_COUNT);
  const chosenShorts = pickShortsToReachTarget(sortedShorts, ENV_TOPIC_TARGET_SHORT_MARKS);
  // Present the paper in ascending mark order: shorts first (warm-up), then 9-markers.
  const questions    = [...chosenNines, ...chosenShorts]
    .sort((a, b) => (a.marks ?? 0) - (b.marks ?? 0));
  const totalMarks   = questions.reduce((sum, q) => sum + (q.marks ?? 0), 0);
  return { questions, totalMarks };
}

// GCSE science excludes MCQ (quiz feature exists for that) and complete_figure
// (requires drawing). All other archetypes are answerable as text.
const GCSE_EXCLUDED_ARCHETYPES = new Set(['mcq', 'complete_figure']);

// Paper number is encoded in the source_paper code: B1H/C1H/P1H = Paper 1,
// B2H/C2H/P2H = Paper 2. null means no filter (draw from both papers).
function gcsePaperFilter(paper) {
  if (paper === 1) return (q) => /[BCP]1H/.test(q.source_paper ?? '');
  if (paper === 2) return (q) => /[BCP]2H/.test(q.source_paper ?? '');
  return () => true;
}

function selectGcseScienceShortSprint(subject, paper) {
  const matchesPaper = gcsePaperFilter(paper);
  const pool = (gcseQuestions[subject] ?? []).filter(
    (q) => !GCSE_EXCLUDED_ARCHETYPES.has(q.science_archetype) && matchesPaper(q),
  );
  return shuffleArray(pool).slice(0, 6);
}

function selectGcseScienceMixedMock(subject, paper) {
  const matchesPaper = gcsePaperFilter(paper);
  const shortPool = (gcseQuestions[subject] ?? []).filter(
    (q) => !GCSE_EXCLUDED_ARCHETYPES.has(q.science_archetype) && q.archetype !== 'gcse_science_extended_6' && matchesPaper(q),
  );
  const extendedPool = (gcseQuestions[subject] ?? []).filter(
    (q) => q.archetype === 'gcse_science_extended_6' && matchesPaper(q),
  );
  const shorts   = shuffleArray(shortPool).slice(0, 5);
  const extended = shuffleArray(extendedPool).slice(0, 1);
  return [...shorts, ...extended];
}

function selectFullMockQuestions(geographyType) {
  const topics = GEOGRAPHY_TOPICS[geographyType] ?? GEOGRAPHY_TOPICS.physical;
  const selected = [];
  for (const topic of topics) {
    for (const archetype of FULL_MOCK_ARCHETYPES) {
      // data_6 and assess_6 are mutually exclusive — only pick one per topic
      if (archetype === 'data_6') {
        const alreadyHasSix = selected.some(
          (q) => q.topic === topic && (q.archetype === 'assess_6' || q.archetype === 'data_6'),
        );
        if (alreadyHasSix) continue;
      }
      const pool = questions.filter((q) => q.archetype === archetype && q.topic === topic);
      if (pool.length) selected.push(shuffleArray(pool)[0]);
    }
  }
  return selected;
}

// ─── State ────────────────────────────────────────────────────────────────────

const INITIAL_STATE = {
  phase: 'lobby',           // 'lobby' | 'preparing' | 'active' | 'processing'
  mockType: 'four_marker',  // 'four_marker' | 'full_mock' | 'short_sprint' | 'mixed_mock' | 'topic_mock'
  group: null,              // env-science content-group code (e.g. '3.1') when mockType === 'topic_mock'
  questions: [],
  currentIndex: 0,
  answers: {},              // { [questionId]: { steps: {}, timeSpentSeconds: 0 } }
  questionEnteredAt: null,
  examStartedAt: null,
  examDuration: FOUR_MARKER_DURATION,
  submitting: false,
  examId: null,
  submitError: null,
  prepareError: null,
  results: [],
  revealedUpTo: -1,
};

// ─── Reducer ─────────────────────────────────────────────────────────────────

function reducer(state, action) {
  switch (action.type) {
    case 'START_EXAM': {
      const mockType = action.mockType ?? 'four_marker';
      const subject  = action.subject ?? 'geography';

      // env-science Topic Mock needs attempt history from the server before
      // selecting questions — handled by the async effect that watches
      // phase: 'preparing'.
      if (subject === 'env-science' && mockType === 'topic_mock') {
        return {
          ...INITIAL_STATE,
          phase: 'preparing',
          mockType,
          group: action.group ?? null,
          examDuration: ENV_TOPIC_DURATION,
        };
      }

      let examDuration;
      let selected;
      if (GCSE_SCIENCE_SUBJECTS.has(subject)) {
        examDuration = mockType === 'mixed_mock' ? GCSE_SCIENCE_MIXED_DURATION : GCSE_SCIENCE_SHORT_DURATION;
        selected = mockType === 'mixed_mock'
          ? selectGcseScienceMixedMock(subject, action.paper ?? null)
          : selectGcseScienceShortSprint(subject, action.paper ?? null);
      } else if (subject === 'env-science') {
        examDuration = mockType === 'mixed_mock' ? ENV_MIXED_DURATION : ENV_SHORT_DURATION;
        selected = mockType === 'mixed_mock'
          ? selectEnvSciMixedMock()
          : selectEnvSciShortSprint();
      } else {
        examDuration = mockType === 'full_mock' ? FULL_MOCK_DURATION : FOUR_MARKER_DURATION;
        selected = mockType === 'full_mock'
          ? selectFullMockQuestions(action.geographyType)
          : selectFourMarkerQuestions(action.geographyType);
      }
      const answers = {};
      selected.forEach((q) => {
        answers[q.id] = { steps: emptySteps(q.archetype || 'outline_4'), timeSpentSeconds: 0 };
      });
      return {
        ...INITIAL_STATE,
        phase: 'active',
        mockType,
        examDuration,
        questions: selected,
        answers,
        examStartedAt: action.timestamp,
        questionEnteredAt: action.timestamp,
        currentIndex: 0,
      };
    }

    case 'PREPARE_READY': {
      // Async selection finished — flip to active with the chosen questions.
      const selected = action.questions;
      const answers = {};
      selected.forEach((q) => {
        answers[q.id] = { steps: emptySteps(q.archetype || 'outline_4'), timeSpentSeconds: 0 };
      });
      return {
        ...state,
        phase: 'active',
        questions: selected,
        answers,
        examStartedAt: action.timestamp,
        questionEnteredAt: action.timestamp,
        currentIndex: 0,
        prepareError: null,
      };
    }

    case 'PREPARE_ERROR':
      return { ...state, phase: 'lobby', prepareError: action.error };

    case 'UPDATE_ANSWER': {
      const q = state.questions.find((q) => q.id === action.questionId);
      const archetype = q?.archetype || 'outline_4';
      const existing = state.answers[action.questionId] ?? { steps: emptySteps(archetype), timeSpentSeconds: 0 };
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: {
            ...existing,
            steps: { ...existing.steps, [action.cardLabel]: action.value },
          },
        },
      };
    }

    case 'NAVIGATE': {
      const currentQ = state.questions[state.currentIndex];
      const elapsed = Math.round((action.timestamp - (state.questionEnteredAt ?? action.timestamp)) / 1000);
      const archetype = currentQ?.archetype || 'outline_4';
      const currentAnswer = state.answers[currentQ?.id] ?? { steps: emptySteps(archetype), timeSpentSeconds: 0 };
      const newAnswers = currentQ
        ? {
            ...state.answers,
            [currentQ.id]: {
              ...currentAnswer,
              timeSpentSeconds: currentAnswer.timeSpentSeconds + elapsed,
            },
          }
        : state.answers;
      return {
        ...state,
        answers: newAnswers,
        currentIndex: Math.max(0, Math.min(state.questions.length - 1, action.index)),
        questionEnteredAt: action.timestamp,
      };
    }

    case 'SUBMIT_EXAM': {
      if (state.submitting) return state;
      const currentQ = state.questions[state.currentIndex];
      const elapsed = Math.round(((action.timestamp ?? Date.now()) - (state.questionEnteredAt ?? Date.now())) / 1000);
      const archetype = currentQ?.archetype || 'outline_4';
      const currentAnswer = state.answers[currentQ?.id] ?? { steps: emptySteps(archetype), timeSpentSeconds: 0 };
      const newAnswers = currentQ
        ? {
            ...state.answers,
            [currentQ.id]: {
              ...currentAnswer,
              timeSpentSeconds: currentAnswer.timeSpentSeconds + elapsed,
            },
          }
        : state.answers;
      return {
        ...state,
        phase: 'processing',
        submitting: true,
        submitError: null,
        answers: newAnswers,
      };
    }

    case 'SUBMIT_SUCCESS':
      return { ...state, submitting: false, examId: action.examId };

    case 'SUBMIT_ERROR':
      return { ...state, submitting: false, phase: 'active', submitError: action.error };

    case 'POLL_RESULT':
      return { ...state, results: action.results };

    case 'ALL_COMPLETE':
      return { ...state, results: action.results };

    case 'REVEAL_NEXT':
      return { ...state, revealedUpTo: Math.min(state.questions.length - 1, state.revealedUpTo + 1) };

    case 'EXIT_EXAM':
      return INITIAL_STATE;

    default:
      return state;
  }
}

// ─── localStorage init ───────────────────────────────────────────────────────

function initFromLocalStorage() {
  try {
    const saved = localStorage.getItem('mockExamInProgress');
    if (!saved) return INITIAL_STATE;
    const parsed = JSON.parse(saved);
    if (!['active', 'processing'].includes(parsed.phase)) return INITIAL_STATE;
    return {
      ...INITIAL_STATE,
      phase: parsed.phase,
      mockType: parsed.mockType ?? 'four_marker',
      examDuration: parsed.examDuration ?? FOUR_MARKER_DURATION,
      questions: parsed.questions ?? [],
      answers: parsed.answers ?? {},
      examStartedAt: parsed.examStartedAt ?? null,
      questionEnteredAt: Date.now(),
      currentIndex: parsed.currentIndex ?? 0,
      examId: parsed.examId ?? null,
    };
  } catch {
    return INITIAL_STATE;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useMockExam(getToken) {
  const [state, dispatch] = useReducer(reducer, undefined, initFromLocalStorage);
  const [remainingSeconds, setRemainingSeconds] = useState(
    state.examDuration ?? FOUR_MARKER_DURATION,
  );

  // Persist state to localStorage. Skip the transient 'preparing' phase —
  // we only want to resume an exam that actually started.
  useEffect(() => {
    if (state.phase === 'lobby' || state.phase === 'preparing') {
      localStorage.removeItem('mockExamInProgress');
    } else {
      localStorage.setItem(
        'mockExamInProgress',
        JSON.stringify({
          phase: state.phase,
          mockType: state.mockType,
          examDuration: state.examDuration,
          questions: state.questions,
          answers: state.answers,
          examStartedAt: state.examStartedAt,
          currentIndex: state.currentIndex,
          examId: state.examId,
        }),
      );
    }
  }, [state]);

  // Countdown timer
  useEffect(() => {
    if (state.phase !== 'active' || !state.examStartedAt) return;
    const duration = state.examDuration ?? FOUR_MARKER_DURATION;
    const id = setInterval(() => {
      const remaining = Math.max(
        0,
        duration - Math.floor((Date.now() - state.examStartedAt) / 1000),
      );
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        dispatch({ type: 'SUBMIT_EXAM', timestamp: Date.now() });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [state.phase, state.examStartedAt, state.examDuration]);

  // Reset remaining seconds when exam starts or restores
  useEffect(() => {
    if (state.phase === 'active' && state.examStartedAt) {
      const duration = state.examDuration ?? FOUR_MARKER_DURATION;
      const remaining = Math.max(
        0,
        duration - Math.floor((Date.now() - state.examStartedAt) / 1000),
      );
      setRemainingSeconds(remaining);
    }
  }, [state.phase, state.examStartedAt, state.examDuration]);

  // Async prep for env-science Topic Mock: fetch attempt history for the
  // candidate question pool, then pick the paper and start the exam.
  useEffect(() => {
    if (state.phase !== 'preparing' || state.mockType !== 'topic_mock' || !state.group) return;

    let cancelled = false;
    (async () => {
      try {
        const candidateIds = envSciQuestions
          .filter((q) => questionGroup(q) === state.group)
          .map((q) => q.id);
        let history = {};
        if (candidateIds.length > 0) {
          const token = await getToken();
          if (cancelled) return;
          const res = await fetch(`${API_BASE}/api/mock-exam/attempt-history`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ questionIds: candidateIds }),
          });
          if (cancelled) return;
          if (res.ok) {
            const data = await res.json();
            history = data.history ?? {};
          }
          // On non-OK, fall through with empty history → behaves as if all
          // questions are unanswered (random tiebreak picks the paper).
        }
        const result = selectEnvSciTopicMock(state.group, history);
        if (cancelled) return;
        if (result.error) {
          dispatch({
            type: 'PREPARE_ERROR',
            error: `Not enough 9-mark questions in this content group (need 2, found ${result.available}). Pick another.`,
          });
          return;
        }
        dispatch({ type: 'PREPARE_READY', questions: result.questions, timestamp: Date.now() });
      } catch (err) {
        if (!cancelled) {
          dispatch({ type: 'PREPARE_ERROR', error: err.message || 'Failed to prepare exam' });
        }
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.mockType, state.group]);

  // Submit to backend
  useEffect(() => {
    if (!state.submitting || state.examId) return;

    let cancelled = false;
    const answers = state.questions.map((q) => ({
      question: q,
      steps: state.answers[q.id]?.steps ?? emptySteps(q.archetype || 'outline_4'),
      timeSpentSeconds: state.answers[q.id]?.timeSpentSeconds ?? 0,
    }));

    (async () => {
      try {
        const token = await getToken();
        if (cancelled) return;
        const res = await fetch(`${API_BASE}/api/mock-exam/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ answers }),
        });
        if (cancelled) return;
        if (!res.ok) throw new Error(`Submit failed: ${res.status}`);
        const { examId } = await res.json();
        if (!cancelled) dispatch({ type: 'SUBMIT_SUCCESS', examId });
      } catch (err) {
        if (!cancelled) dispatch({ type: 'SUBMIT_ERROR', error: err.message });
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.submitting, state.examId]);

  // Poll for results
  useEffect(() => {
    if (state.phase !== 'processing' || !state.examId) return;

    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const token = await getToken();
        if (!active) return;
        const res = await fetch(`${API_BASE}/api/mock-exam/${state.examId}/results`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active || !res.ok) return;
        const data = await res.json();
        if (!active) return;
        if (data.allComplete) {
          dispatch({ type: 'ALL_COMPLETE', results: data.questions });
        } else {
          dispatch({ type: 'POLL_RESULT', results: data.questions });
        }
      } catch (err) {
        console.error('Mock exam poll error:', err);
      }
    };

    poll();
    const id = setInterval(poll, 3000);
    return () => { active = false; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.examId]);

  // Sequential reveal auto-advance
  useEffect(() => {
    if (!state.results.length) return;
    const nextIdx = state.revealedUpTo + 1;
    if (nextIdx >= state.questions.length) return;
    const nextQuestion = state.questions[nextIdx];
    if (!nextQuestion) return;
    const next = state.results.find((r) => r.questionId === nextQuestion.id);
    if (!next) return;
    if (next.markingStatus !== 'completed' && next.markingStatus !== 'error') return;

    const t = setTimeout(() => {
      dispatch({ type: 'REVEAL_NEXT' });
    }, 700);
    return () => clearTimeout(t);
  }, [state.results, state.revealedUpTo, state.questions]);

  return { state, dispatch, remainingSeconds };
}
